'use client';

import React, { FC, useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { useForm } from 'react-hook-form';
import ResizeObserver from 'resize-observer-polyfill';
import * as yup from 'yup';
import { InferType } from 'yup';

import Input from '@/components/input.component';
import PostMessageOnResizeWrapper from '@/hooks/PostMessageOnResizeWrapper';

import PricingCalculator from './calculator';

export const schema = yup.object().shape({
  input: yup
    .number()
    .transform((value) => (isNaN(value) || value <= 0 ? 1 : value))
    .required(),
  output: yup
    .number()
    .transform((value) => (isNaN(value) || value <= 0 ? 1 : value))
    .required(),
  api: yup
    .number()
    .transform((value) => (isNaN(value) || value <= 0 ? 1 : value))
    .required(),
});
export type CalculatorFormData = InferType<typeof schema>;

interface Option {
  name: string;
  multiplier: number;
  index: number;
}

const CalculatorForm: FC<{ t: any }> = ({ t }) => {
  const tokenTypes = t.calculateByOptions.map(
    (option: string, index: number) => ({
      name: option,
      multiplier: index === 0 ? 1 : index === 1 ? 1.3333333333 : 0.25,
      index: index,
    }),
  );

  const tokenNames = t.calculateByOptionsHeader.map(
    (option: string, index: number) => ({
      name: option,
    }),
  );

  const [selectedOption, setSelectedOption] = useState<Option>(tokenTypes[0]);
  const [showErrors, setShowErrors] = useState(false);

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
    setValue,
  } = useForm<CalculatorFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      input: 100,
      output: 100,
      api: 100,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const formData = watch();

  // Sanitize input values to ensure they're always valid
  useEffect(() => {
    if (!formData.input || formData.input <= 0)
      setValue('input', 1, { shouldValidate: false });
    if (!formData.output || formData.output <= 0)
      setValue('output', 1, { shouldValidate: false });
    if (!formData.api || formData.api <= 0)
      setValue('api', 1, { shouldValidate: false });
  }, [formData, setValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowErrors(true); // Only show errors after user interaction

    const value = parseInt(e.target.value);
    if (isNaN(value) || value <= 0) {
      // Just let the validation handle it, but ensure we display the error
      return;
    }
  };

  const handleOptionChange = (optionName: string) => {
    const foundOption = tokenTypes.find((option) => option.name === optionName);

    if (foundOption) {
      setShowErrors(false); // Hide errors when switching options
      setSelectedOption(foundOption);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>();

  const onResize = (width: number) => {
    const message = {
      width,
    };
    window.parent.postMessage(message, '*');
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (
        containerRef.current.offsetWidth &&
        containerRef?.current?.offsetWidth !== width
      ) {
        setWidth(containerRef.current.offsetWidth);
        onResize(containerRef.current.offsetWidth);
      }
    });

    resizeObserver.observe(containerRef.current);

    return function cleanup() {
      resizeObserver.disconnect();
    };
  }, [width]);

  // Ensure sanitized values for calculator
  const safeInputTokens =
    formData.input && formData.input > 0 ? formData.input : 1;
  const safeOutputTokens =
    formData.output && formData.output > 0 ? formData.output : 1;
  const safeApiCalls = formData.api && formData.api > 0 ? formData.api : 1;

  return (
    <PostMessageOnResizeWrapper>
      <form onSubmit={handleSubmit(() => {})}>
        <div
          ref={containerRef}
          style={{ fontFamily: 'Satoshi, sans-serif' }}
          className="mx-auto max-w-7xl p-4 md:p-6"
        >
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-md">
            {/* Input Parameters Section */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 border-b border-gray-200 p-4 md:p-5">
              {/* Input Fields Grid */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <label
                    htmlFor="input"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.inputTokensLabel} {tokenNames[selectedOption.index].name}
                  </label>
                  <Input
                    type="number"
                    name="input"
                    id="input"
                    autoComplete="off"
                    className="block w-full rounded-md border-gray-300 text-center text-sm font-medium shadow-sm transition duration-150 ease-in-out focus:border-black focus:ring-black"
                    {...register('input', {
                      valueAsNumber: true,
                      onChange: handleInputChange,
                      min: 1,
                    })}
                  />
                  {showErrors && errors.input && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.input.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="output-tokens"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.outputTokensLabel}{' '}
                    {tokenNames[selectedOption.index].name}
                  </label>
                  <Input
                    type="number"
                    id="output-tokens"
                    name="output-tokens"
                    autoComplete="off"
                    className="block w-full rounded-md border-gray-300 text-center text-sm font-medium shadow-sm transition duration-150 ease-in-out focus:border-black focus:ring-black"
                    {...register('output', {
                      valueAsNumber: true,
                      onChange: handleInputChange,
                      min: 1,
                    })}
                  />
                  {showErrors && errors.output && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.output.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="api"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.apiCallsLabel}
                  </label>
                  <Input
                    type="number"
                    id="api"
                    name="api"
                    autoComplete="off"
                    className="block w-full rounded-md border-gray-300 text-center text-sm font-medium shadow-sm transition duration-150 ease-in-out focus:border-black focus:ring-black"
                    {...register('api', {
                      valueAsNumber: true,
                      onChange: handleInputChange,
                      min: 1,
                    })}
                  />
                  {showErrors && errors.api && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.api.message}
                    </p>
                  )}
                </div>
              </div>
              {/* Token Type Selection */}
              <div className="">
                <h3 className="mb-2 text-sm font-medium text-gray-700">
                  {t.calculateByLabel}
                </h3>
                <div
                  className="inline-flex rounded-md shadow-sm"
                  role="radiogroup"
                >
                  {tokenTypes.map((option) => (
                    <div
                      key={option.name}
                      className={cn(
                        'cursor-pointer border px-2.5 py-1.5 text-sm font-medium transition-colors duration-150 ease-in-out focus:outline-none',
                        {
                          'border-gray-800 bg-gray-800 text-white':
                            selectedOption.name === option.name,
                          'border-gray-300 bg-white text-gray-700 hover:bg-gray-100':
                            selectedOption.name !== option.name,
                        },
                        'flex items-center justify-center first:rounded-l-md last:rounded-r-md',
                      )}
                      role="radio"
                      aria-checked={selectedOption.name === option.name}
                      onClick={() => handleOptionChange(option.name)}
                    >
                      {option.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Table Section */}
            <PricingCalculator
              t={t}
              selectedOption={selectedOption}
              inputTokens={safeInputTokens}
              outputTokens={safeOutputTokens}
              apiCalls={safeApiCalls}
            />
          </div>
        </div>
      </form>
    </PostMessageOnResizeWrapper>
  );
};

export default CalculatorForm;
